import { listTutorialProgress, putTutorialProgress } from '@/features/a0/api/adapters/preferences'
import { toApiError } from '@/features/a0/api/errors'
import { scopedRequest } from '@/features/a0/identity/identity'
import type { ChapterRecord, ChapterStatus } from './machine'

// Tutorial progress lives in the Z08 per-user, per-chapter rows (version "1"). The browser copy is only
// a fallback for a failed load; the logout sweep clears it with every other user key.
export const TUTORIAL_VERSION = '1'
export const cacheKey = (userId: string): string => `zupa-tutorial-v${TUTORIAL_VERSION}:${userId}`

type Chapters = Record<string, ChapterRecord>
export type SaveResult = { kind: 'saved' } | { kind: 'conflict'; chapters: Chapters } | { kind: 'failed' }

interface Row {
  chapter_id: string
  version: number
  status: string
  step_id?: string | null
  viewed_step_ids: unknown[]
  practiced_step_ids: unknown[]
}

const ids = (list: unknown[]): string[] => list.filter((x): x is string => typeof x === 'string')

function readCache(userId: string): Chapters {
  try {
    return JSON.parse(localStorage.getItem(cacheKey(userId)) ?? '{}') as Chapters
  } catch {
    return {}
  }
}

function writeCache(userId: string, chapters: Chapters): void {
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify(chapters))
  } catch {
    // storage full or blocked: the server rows stay the truth
  }
}

export function createProgressSync(userId: string) {
  const versions: Record<string, number> = {}
  let chapters: Chapters = {}
  let queue: Promise<unknown> = Promise.resolve()

  async function fetchRows(): Promise<Row[]> {
    const rows = await scopedRequest('user', (signal) => listTutorialProgress({ query: { tutorial_version: TUTORIAL_VERSION }, signal }))
    return rows as Row[]
  }

  function adopt(rows: Row[]): Chapters {
    chapters = {}
    for (const r of rows) {
      versions[r.chapter_id] = r.version
      chapters[r.chapter_id] = { status: r.status as ChapterStatus, stepId: r.step_id ?? null, viewed: ids(r.viewed_step_ids), practiced: ids(r.practiced_step_ids) }
    }
    writeCache(userId, chapters)
    return chapters
  }

  async function load(): Promise<{ chapters: Chapters; hasRows: boolean }> {
    try {
      const rows = await fetchRows()
      return { chapters: adopt(rows), hasRows: rows.length > 0 }
    } catch {
      chapters = readCache(userId)
      return { chapters, hasRows: Object.keys(chapters).length > 0 }
    }
  }

  async function put(chapter: string, rec: ChapterRecord): Promise<SaveResult> {
    const status = rec.status
    if (status === 'not_started') return { kind: 'saved' }
    try {
      const row = await scopedRequest('user', (signal) => putTutorialProgress({
        path: { tutorial_version: TUTORIAL_VERSION, chapter_id: chapter },
        body: { expected_version: versions[chapter] ?? 0, step_id: rec.stepId, status, viewed_step_ids: rec.viewed, practiced_step_ids: rec.practiced },
        signal,
      }))
      versions[chapter] = (row as Row).version
      chapters = { ...chapters, [chapter]: rec }
      writeCache(userId, chapters)
      return { kind: 'saved' }
    } catch (error) {
      if (toApiError(error).status !== 409) return { kind: 'failed' }
      try {
        return { kind: 'conflict', chapters: adopt(await fetchRows()) }
      } catch {
        return { kind: 'failed' }
      }
    }
  }

  /** Saves run one at a time so each carries the version the previous one produced. */
  function save(chapter: string, rec: ChapterRecord): Promise<SaveResult> {
    const next = queue.then(() => put(chapter, rec))
    queue = next.catch(() => undefined)
    return next
  }

  return { load, save }
}
